/**
 * The colours a design is actually made of — the "Selection colour presets" list.
 *
 * A palette recolours EVERYTHING onto a ramp, which is the right tool for "make
 * this card blue" and the wrong one for "the black in this frame should be navy".
 * This module answers the second question: read the colours back off the scope,
 * and swap one of them everywhere at once — so a listed swatch behaves like a
 * global colour for the design rather than a per-element fill.
 *
 * TWO things keep those globals apart, because a list keyed by colour VALUE alone
 * collapses the moment two entries meet:
 *
 *  - TOKENS. Editing a row stamps a small id (`colorTokens` on the element or
 *    frame, `token` on a text span) onto every occurrence it covered. From then
 *    on the row is that token, not that colour — so dragging one row through
 *    another's colour no longer swallows it, and dragging back out again leaves
 *    both intact. Untouched artwork carries no token and is grouped by value,
 *    which is right: two identical blacks a designer drew ARE one black.
 *  - GROUPS. Colours are listed per kind of thing — frames, text, shapes, images,
 *    tables, codes — so the text black and the shape black are separate globals
 *    even before either has been touched.
 *
 * Grouping ignores ALPHA. A 60%-black rule and a solid black heading are the same
 * black to a designer, and listing them apart would mean editing black twice.
 * Each occurrence keeps its own alpha through a swap, so the faded rule stays
 * faded — unless the picked colour carries alpha itself, which is how you fade a
 * colour on purpose.
 *
 * Same coverage as the palettes (utils/paletteRecolor) plus the per-character
 * text fills, and deliberately NOT shadow / effect colours: a soft black shadow
 * turning navy along with the headline reads as a bug, not a global colour.
 * Raster image pixels are not ours to rewrite either — those follow a palette.
 */
import {
    parseRgba, borderWidthOf, isGradientBorder, isGradientText, getStrokes,
    BORDER_KEYS, SHAPE_BORDER_KEYS, TEXT_FILL_KEYS,
} from '../panels/propertyControls';
import { createId, ELEMENT_TYPES } from '../schema/documentSchema';
import { PALETTE_COLOR_KEYS, PALETTE_FRAME_KEYS } from './paletteRecolor';
import { elementColorSpans } from './textRuns';

/**
 * Every style key that holds a colour a user can see.
 *
 * The palette keys cover solid fills; gradients live under a per-fill prefix
 * (border / text / stroke → `borderGradientColor1`…), which is how one element
 * can carry its own gradient and a gradient border without them colliding.
 */
export const COLOR_USAGE_STYLE_KEYS = [
    ...PALETTE_COLOR_KEYS,
    'borderGradientColor1',
    'borderGradientColor2',
    'textGradientColor1',
    'textGradientColor2',
    'strokeGradientColor1',
    'strokeGradientColor2',
];

/** The tabs, in the order they are offered. Empty ones are not shown. */
export const COLOR_GROUPS = [
    { id: 'frame', label: 'Frame' },
    { id: 'text', label: 'Text' },
    { id: 'shape', label: 'Shapes' },
    { id: 'image', label: 'Images' },
    { id: 'table', label: 'Tables' },
    { id: 'code', label: 'Codes' },
];

const GROUP_BY_TYPE = {
    [ELEMENT_TYPES.TEXT]: 'text',
    [ELEMENT_TYPES.PLACEHOLDER]: 'text',
    [ELEMENT_TYPES.SHAPE]: 'shape',
    [ELEMENT_TYPES.IMAGE]: 'image',
    [ELEMENT_TYPES.TABLE]: 'table',
    [ELEMENT_TYPES.QRCODE]: 'code',
    [ELEMENT_TYPES.BARCODE]: 'code',
};

/** Which tab an element's colours belong to. Unknown types read as shapes. */
export function groupForElement(el) {
    return GROUP_BY_TYPE[el?.type] || 'shape';
}

const hex2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');

/**
 * The value a colour is grouped and matched by: `#rrggbb`, alpha dropped.
 * Null for anything that paints nothing — 'transparent', 'none', '', garbage.
 */
export function colorKey(color) {
    const c = parseRgba(color);
    if (!c || !(c.a > 0)) return null;
    return `#${hex2(c.r)}${hex2(c.g)}${hex2(c.b)}`;
}

/** A fresh identity for a row that has never been edited. */
export function newColorToken() {
    return createId('ctok');
}

/** Perceptual lightness 0..1, only used to order the list. */
function luma(key) {
    const c = parseRgba(key);
    return c ? (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255 : 0;
}

/**
 * A row's identity.
 *
 * The value is part of it even for a tokenised row: an element whose colour was
 * later changed on its own (the Design tab, a palette on ONE frame) has left the
 * global behind, and listing it under the row it no longer matches would mean a
 * swatch that lies about what it will change.
 */
function rowId(group, token, key) {
    return token ? `t:${token}:${key}` : `v:${group}:${key}`;
}

/** Does this occurrence belong to the row `id`? */
function matches(id, group, occ) {
    const key = colorKey(occ.color);
    if (!key) return false;
    return rowId(group, occ.token || null, key) === id;
}

/** New value for one occurrence — the picked colour, with alpha resolved. */
function applied(color, to) {
    const src = parseRgba(color);
    const dst = parseRgba(to);
    if (!src || !dst) return color;
    // The picked colour's alpha wins when it carries one (that IS the edit);
    // otherwise each occurrence keeps the transparency it was drawn with.
    const a = dst.a < 1 ? dst.a : src.a;
    const rgb = `#${hex2(dst.r)}${hex2(dst.g)}${hex2(dst.b)}`;
    return a >= 1 ? rgb : `${rgb}${hex2(a * 255)}`;
}

/* ------------------------------------------------------------------ *
 *  Occurrences — every single place a colour is painted
 * ------------------------------------------------------------------ */

/**
 * `path` is how the occurrence is addressed when a token is written back:
 * `styles.<key>`, `strokes.<i>` and `spans.<i>.color1|2` live in the owner's
 * `colorTokens` map, while a span's own colour carries its token INSIDE the span
 * (utils/textRuns keeps it through edits and refuses to merge two runs that
 * disagree about it).
 */
function frameOccurrences(frame) {
    const tokens = frame?.colorTokens || {};
    // An artboard is either a flat colour or a gradient — the other one's keys
    // are still stored, and listing them offered a swatch that painted nothing.
    const gradient = frame?.backgroundType === 'linear' || frame?.backgroundType === 'radial';
    const painted = (k) => (k === 'background' ? !gradient : gradient);
    return PALETTE_FRAME_KEYS
        .filter(painted)
        .map((k) => ({ path: k, color: frame?.[k], token: tokens[k] || null }))
        .filter((o) => colorKey(o.color));
}

/**
 * Is this style key actually PAINTED right now?
 *
 * A style sheet always carries the full set of keys — a shape keeps a stroke
 * colour at zero width, a text box keeps gradient stops while its fill is solid.
 * Listing those put swatches in the panel that changed nothing on the canvas,
 * which is the worst thing a colour list can do: it says "this is one of the
 * colours your design is made of" about a colour nobody can see.
 *
 * The tests are the renderers' own (`borderWidthOf`, `isGradientBorder`,
 * `isGradientText`, and `backgroundType || fillType` — see propertyControls), so
 * this list can only drift from what is drawn if the drawing rules change.
 */
function isPaintedKey(styles, key) {
    // `backgroundType` (text / image boxes) wins over `fillType` (shapes), which
    // is exactly what resolveFillBackground does.
    const fillType = styles.backgroundType || styles.fillType || 'solid';
    const fillIsGradient = fillType === 'linear' || fillType === 'radial';

    switch (key) {
        // Solid fills — off while their family is drawing a gradient instead.
        case 'fill':
        case 'backgroundColor':
            return !fillIsGradient;
        case 'gradientColor1':
        case 'gradientColor2':
            return fillIsGradient;

        // Text colour and its gradient are the same either/or.
        case 'color':
            return !isGradientText(styles);
        case 'textGradientColor1':
        case 'textGradientColor2':
            return isGradientText(styles);

        // A border at zero width paints nothing, whatever colour it holds.
        case 'borderColor':
            return borderWidthOf(styles, BORDER_KEYS) > 0 && !isGradientBorder(styles, BORDER_KEYS);
        case 'borderGradientColor1':
        case 'borderGradientColor2':
            return isGradientBorder(styles, BORDER_KEYS);

        // Same again for a shape's outline, under its own key names.
        case 'stroke':
            return borderWidthOf(styles, SHAPE_BORDER_KEYS) > 0
                && !isGradientBorder(styles, SHAPE_BORDER_KEYS);
        case 'strokeGradientColor1':
        case 'strokeGradientColor2':
            return isGradientBorder(styles, SHAPE_BORDER_KEYS);

        // QR / barcode / table paint theirs unconditionally; a transparent value
        // is already dropped by `colorKey`.
        default:
            return true;
    }
}

function elementOccurrences(el) {
    const styles = el?.styles || {};
    const tokens = el?.colorTokens || {};
    const out = [];

    COLOR_USAGE_STYLE_KEYS.forEach((k) => {
        if (!isPaintedKey(styles, k)) return;
        const path = `styles.${k}`;
        out.push({ path, color: styles[k], token: tokens[path] || null });
    });
    // Text outlines are a list of { color } rather than one key. Hidden ones and
    // a zero stroke weight paint nothing, so `getStrokes` decides here too.
    if (Array.isArray(styles.strokes) && getStrokes(styles).length) {
        styles.strokes.forEach((s, i) => {
            if (s?.visible === false) return;
            const path = `strokes.${i}`;
            out.push({ path, color: s?.color, token: tokens[path] || null });
        });
    }
    // Per-character fills — the one blue word inside a black headline.
    elementColorSpans(el, el?.content).forEach((s, i) => {
        out.push({ path: `spans.${i}.color`, color: s.color, token: s.token || null });
        if (s.fill) {
            const p1 = `spans.${i}.color1`;
            const p2 = `spans.${i}.color2`;
            out.push({ path: p1, color: s.fill.color1, token: tokens[p1] || null });
            out.push({ path: p2, color: s.fill.color2, token: tokens[p2] || null });
        }
    });

    return out.filter((o) => colorKey(o.color));
}

/* ------------------------------------------------------------------ *
 *  Reading the list
 * ------------------------------------------------------------------ */

/**
 * Every colour in scope, most-used first.
 *
 * Returns `[{ id, group, color, count, token }]`. `id` is what a swap is aimed
 * at; `color` is what the swatch paints. They are separate because an edit in
 * flight moves the colour while the row has to stay put.
 */
export function collectColorUsage(frames = [], elements = []) {
    const seen = new Map();

    const take = (group, occ) => {
        const key = colorKey(occ.color);
        if (!key) return;
        const id = rowId(group, occ.token, key);
        const hit = seen.get(id);
        if (hit) hit.count += 1;
        else {
            seen.set(id, {
                id, group, color: key, count: 1, token: occ.token || null,
            });
        }
    };

    frames.forEach((f) => frameOccurrences(f).forEach((o) => take('frame', o)));
    elements.forEach((el) => {
        const group = groupForElement(el);
        elementOccurrences(el).forEach((o) => take(group, o));
    });

    // Most-used first, then light → dark, so a tab reads like the design's own
    // hierarchy: the paper and the body colour on top, one-off accents below.
    return [...seen.values()].sort((a, b) => b.count - a.count || luma(b.color) - luma(a.color));
}

/* ------------------------------------------------------------------ *
 *  Writing a swap
 * ------------------------------------------------------------------ */

/**
 * Patch for one frame, or null when the row isn't painted in it.
 *
 * `token` is the identity the row takes from here on — the caller mints one per
 * edit so every occurrence across every frame joins the SAME global.
 */
export function replaceFrameColorPatch(frame, id, to, token) {
    const occs = frameOccurrences(frame).filter((o) => matches(id, 'frame', o));
    if (!occs.length) return null;

    const patch = {};
    const tokens = { ...(frame?.colorTokens || {}) };
    const base = { ...(frame?.presetBase || {}) };
    occs.forEach((o) => {
        // Snapshot on FIRST touch only, so Reset goes back to the artwork rather
        // than to whatever the previous drag happened to leave behind.
        if (base[o.path] === undefined) base[o.path] = o.color;
        patch[o.path] = applied(o.color, to);
        tokens[o.path] = token;
    });
    return { ...patch, colorTokens: tokens, presetBase: base };
}

/**
 * Patch for one element, or null when the row isn't painted on it.
 *
 * `styles` here is a PARTIAL — only the keys that changed — so it merges the
 * same way every other element patch does. `colorSpans` is whole, since a span
 * list only means anything as a list.
 */
export function replaceElementColorPatch(el, id, to, token) {
    const group = groupForElement(el);
    const occs = elementOccurrences(el).filter((o) => matches(id, group, o));
    if (!occs.length) return null;

    const styles = el?.styles || {};
    const tokens = { ...(el?.colorTokens || {}) };
    // Snapshot on FIRST touch only, so Reset goes back to the artwork rather than
    // to whatever the previous drag happened to leave behind. A span is keyed by
    // its TOKEN, not its index: text edits move spans around, and a base recorded
    // against a position would come back on the wrong characters.
    const base = { ...(el?.presetBase || {}) };
    const remember = (key, color) => { if (base[key] === undefined) base[key] = color; };
    const stylePatch = {};
    let strokes = null;
    let spans = null;

    const spanList = elementColorSpans(el, el?.content);

    occs.forEach((o) => {
        const value = applied(o.color, to);

        if (o.path.startsWith('styles.')) {
            remember(o.path, o.color);
            stylePatch[o.path.slice(7)] = value;
            tokens[o.path] = token;
            return;
        }
        if (o.path.startsWith('strokes.')) {
            const i = Number(o.path.slice(8));
            remember(o.path, o.color);
            strokes = strokes || [...(styles.strokes || [])];
            strokes[i] = { ...strokes[i], color: value };
            tokens[o.path] = token;
            return;
        }
        // spans.<i>.color | color1 | color2
        const [, rawIndex, field] = o.path.split('.');
        const i = Number(rawIndex);
        spans = spans || spanList.map((s) => ({ ...s }));
        if (!spans[i]) return;
        if (field === 'color') {
            // A span's identity travels WITH the span, so text edits can't strand it.
            remember(`span:${spans[i].token || token}`, o.color);
            spans[i] = { ...spans[i], color: value, token };
        } else {
            const which = field === 'color1' ? 'color1' : 'color2';
            remember(o.path, o.color);
            spans[i] = { ...spans[i], fill: { ...(spans[i].fill || {}), [which]: value } };
            tokens[o.path] = token;
        }
    });

    if (strokes) stylePatch.strokes = strokes;
    if (!Object.keys(stylePatch).length && !spans) return null;

    return {
        ...(Object.keys(stylePatch).length ? { styles: stylePatch } : null),
        ...(spans ? { colorSpans: spans } : null),
        colorTokens: tokens,
        presetBase: base,
    };
}

/* ------------------------------------------------------------------ *
 *  Reset — back to the colours the artwork was drawn with
 * ------------------------------------------------------------------ */

/** Has anything in scope been recoloured from this list? Drives the Reset button. */
export function hasColorPresetEdits(frames = [], elements = []) {
    const dirty = (o) => o && Object.keys(o).length > 0;
    return frames.some((f) => dirty(f?.presetBase))
        || elements.some((el) => dirty(el?.presetBase));
}

/** Undo every preset swap on one frame: originals back, identities dropped. */
export function resetFrameColorsPatch(frame) {
    const base = frame?.presetBase;
    if (!base || !Object.keys(base).length) return null;
    const patch = {};
    Object.keys(base).forEach((path) => { patch[path] = base[path]; });
    return { ...patch, colorTokens: null, presetBase: null };
}

/**
 * Undo every preset swap on one element.
 *
 * Spans are matched by token, which is how a word that has been retyped or moved
 * since the swap still finds its own original colour.
 */
export function resetElementColorsPatch(el) {
    const base = el?.presetBase;
    if (!base || !Object.keys(base).length) return null;

    const styles = el?.styles || {};
    const stylePatch = {};
    let strokes = null;

    Object.keys(base).forEach((path) => {
        if (path.startsWith('styles.')) stylePatch[path.slice(7)] = base[path];
        else if (path.startsWith('strokes.')) {
            const i = Number(path.slice(8));
            strokes = strokes || [...(styles.strokes || [])];
            if (strokes[i]) strokes[i] = { ...strokes[i], color: base[path] };
        }
    });

    let spans = null;
    const spanList = elementColorSpans(el, el?.content);
    if (spanList.length) {
        const next = spanList.map((s, i) => {
            const own = s.token ? base[`span:${s.token}`] : undefined;
            const g1 = base[`spans.${i}.color1`];
            const g2 = base[`spans.${i}.color2`];
            if (own === undefined && g1 === undefined && g2 === undefined) return s;
            const out = { ...s };
            if (own !== undefined) { out.color = own; delete out.token; }
            if (g1 !== undefined || g2 !== undefined) {
                out.fill = {
                    ...(s.fill || {}),
                    ...(g1 !== undefined ? { color1: g1 } : null),
                    ...(g2 !== undefined ? { color2: g2 } : null),
                };
            }
            return out;
        });
        if (next.some((s, i) => s !== spanList[i])) spans = next;
    }

    if (strokes) stylePatch.strokes = strokes;
    return {
        ...(Object.keys(stylePatch).length ? { styles: stylePatch } : null),
        ...(spans ? { colorSpans: spans } : null),
        colorTokens: null,
        presetBase: null,
    };
}
